import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft, Syringe, TestTube, FileText, Calendar, Phone, Mail,
  User, PawPrint, Dog, Cat, CheckCircle, Plus, Clock, Shield,
  Heart, Activity, Stethoscope
} from "lucide-react";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

const VACCINE_TYPES = [
  { value: "rabies", label: "Antirabbica" },
  { value: "distemper", label: "Cimurro" },
  { value: "parvo", label: "Parvovirosi" },
  { value: "hepatitis", label: "Epatite infettiva" },
  { value: "lepto", label: "Leptospirosi" },
  { value: "parainfluenza", label: "Parainfluenza" },
  { value: "bordetella", label: "Bordetella (tosse)" },
  { value: "combo", label: "Polivalente" },
  { value: "fiv_felv", label: "FIV/FeLV (gatti)" },
  { value: "altro", label: "Altro" }
];

const EXAM_TYPES = [
  { value: "ematologia", label: "Ematologia" },
  { value: "biochimica", label: "Biochimica" },
  { value: "urine", label: "Esame urine" },
  { value: "feci", label: "Esame feci" },
  { value: "imaging_radiografia", label: "Radiografia" },
  { value: "imaging_ecografia", label: "Ecografia" },
  { value: "cardiologia", label: "Cardiologia" },
  { value: "dermatologia", label: "Dermatologia" },
  { value: "citologia", label: "Citologia" },
  { value: "altro", label: "Altro" }
];

export default function VetPatientDetail() {
  const { petId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [showAddVaccine, setShowAddVaccine] = useState(false);
  const [showAddExam, setShowAddExam] = useState(false);
  const [showAddAppointment, setShowAddAppointment] = useState(false);

  useEffect(() => {
    fetchPatientData();
  }, [petId]);

  const fetchPatientData = async () => {
    const token = localStorage.getItem('vet_token');
    if (!token) {
      navigate('/vet/login');
      return;
    }

    try {
      const res = await fetch(`${API}/api/vet/patients/${petId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (res.ok) {
        const result = await res.json();
        setData(result);
      } else {
        toast.error("Paziente non trovato");
        navigate('/vet/dashboard');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="w-12 h-12 border-3 border-[#2E7D32] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const { pet, owner, vaccines, exams, documents, visits, appointments } = data;
  const SpeciesIcon = pet?.species === 'cat' ? Cat : pet?.species === 'dog' ? Dog : PawPrint;

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Header */}
      <header className="bg-white border-b border-black/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link to="/vet/dashboard" className="text-[#86868B] hover:text-[#1D1D1F]">
              <ArrowLeft size={24} />
            </Link>
            <div className="flex-1">
              <h1 className="font-bold text-[#1D1D1F]">{pet?.name}</h1>
              <p className="text-sm text-[#86868B]">{owner?.name}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Pet Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              {/* Photo */}
              <div className="w-32 h-32 mx-auto rounded-2xl bg-[#F5F5F7] flex items-center justify-center overflow-hidden mb-4">
                {pet?.photo_url ? (
                  <img 
                    src={`${API.replace('/api', '')}${pet.photo_url}`}
                    alt={pet.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <SpeciesIcon className="text-[#86868B]" size={48} />
                )}
              </div>

              <h2 className="text-xl font-bold text-center text-[#1D1D1F] mb-1">{pet?.name}</h2>
              <p className="text-center text-[#86868B] mb-4">{pet?.breed || pet?.species}</p>

              {/* Quick Info */}
              <div className="space-y-3">
                {pet?.microchip && (
                  <div className="flex items-center justify-between p-3 bg-[#F5F5F7] rounded-xl">
                    <span className="text-sm text-[#86868B]">Microchip</span>
                    <span className="font-mono text-sm font-medium">{pet.microchip}</span>
                  </div>
                )}
                {pet?.birth_date && (
                  <div className="flex items-center justify-between p-3 bg-[#F5F5F7] rounded-xl">
                    <span className="text-sm text-[#86868B]">Data nascita</span>
                    <span className="text-sm font-medium">
                      {new Date(pet.birth_date).toLocaleDateString('it-IT')}
                    </span>
                  </div>
                )}
                {pet?.sex && (
                  <div className="flex items-center justify-between p-3 bg-[#F5F5F7] rounded-xl">
                    <span className="text-sm text-[#86868B]">Sesso</span>
                    <span className="text-sm font-medium">
                      {pet.sex === 'M' ? 'Maschio' : 'Femmina'}
                    </span>
                  </div>
                )}
              </div>

              {/* Owner Info */}
              <div className="mt-6 pt-6 border-t border-black/5">
                <h3 className="font-semibold text-[#1D1D1F] mb-3 flex items-center gap-2">
                  <User size={16} /> Proprietario
                </h3>
                <p className="font-medium text-[#1D1D1F]">{owner?.name}</p>
                {owner?.phone && (
                  <a href={`tel:${owner.phone}`} className="flex items-center gap-2 text-sm text-[#2E7D32] mt-2 hover:underline">
                    <Phone size={14} /> {owner.phone}
                  </a>
                )}
                {owner?.email && (
                  <a href={`mailto:${owner.email}`} className="flex items-center gap-2 text-sm text-[#2E7D32] mt-1 hover:underline">
                    <Mail size={14} /> {owner.email}
                  </a>
                )}
              </div>

              {/* Actions */}
              <div className="mt-6 space-y-2">
                <Button 
                  onClick={() => setShowAddVaccine(true)}
                  className="w-full bg-[#2E7D32] hover:bg-[#1B5E20] rounded-xl"
                >
                  <Syringe size={18} className="mr-2" /> Registra vaccino
                </Button>
                <Button 
                  onClick={() => setShowAddExam(true)}
                  variant="outline"
                  className="w-full rounded-xl"
                >
                  <TestTube size={18} className="mr-2" /> Registra esame
                </Button>
                <Button 
                  onClick={() => setShowAddAppointment(true)}
                  variant="outline"
                  className="w-full rounded-xl"
                >
                  <Calendar size={18} className="mr-2" /> Prenota appuntamento
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="bg-white p-1 rounded-2xl border border-black/5 mb-6 w-full justify-start">
                <TabsTrigger value="overview" className="rounded-xl data-[state=active]:bg-[#2E7D32] data-[state=active]:text-white">
                  Panoramica
                </TabsTrigger>
                <TabsTrigger value="vaccines" className="rounded-xl data-[state=active]:bg-[#2E7D32] data-[state=active]:text-white">
                  Vaccini ({vaccines?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="exams" className="rounded-xl data-[state=active]:bg-[#2E7D32] data-[state=active]:text-white">
                  Esami ({exams?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="visits" className="rounded-xl data-[state=active]:bg-[#2E7D32] data-[state=active]:text-white">
                  Visite ({visits?.length || 0})
                </TabsTrigger>
              </TabsList>

              {/* Overview */}
              <TabsContent value="overview">
                <div className="bg-white rounded-3xl p-6 shadow-sm">
                  <h3 className="font-bold text-[#1D1D1F] mb-4">Riepilogo salute</h3>

                  {/* Rabies Status */}
                  <div className={`p-4 rounded-2xl mb-4 ${
                    pet?.rabies_vaccine_date 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-amber-50 border border-amber-200'
                  }`}>
                    <div className="flex items-center gap-3">
                      <Shield className={pet?.rabies_vaccine_date ? 'text-green-600' : 'text-amber-600'} size={24} />
                      <div>
                        <p className="font-medium">Vaccinazione Antirabbica</p>
                        {pet?.rabies_vaccine_date ? (
                          <p className="text-sm text-green-700">
                            Ultima: {new Date(pet.rabies_vaccine_date).toLocaleDateString('it-IT')}
                            {pet.rabies_vaccine_expiry && ` • Scade: ${new Date(pet.rabies_vaccine_expiry).toLocaleDateString('it-IT')}`}
                          </p>
                        ) : (
                          <p className="text-sm text-amber-700">Non registrata</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Recent vaccines */}
                  <h4 className="font-semibold text-[#1D1D1F] mt-6 mb-3">Ultimi vaccini</h4>
                  {vaccines?.length > 0 ? (
                    <div className="space-y-2">
                      {vaccines.slice(0, 5).map((v, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-[#F5F5F7] rounded-xl">
                          <div className="flex items-center gap-3">
                            <Syringe size={16} className="text-[#2E7D32]" />
                            <div>
                              <p className="font-medium text-sm">{v.name}</p>
                              <p className="text-xs text-[#86868B]">
                                {new Date(v.date_administered).toLocaleDateString('it-IT')}
                              </p>
                            </div>
                          </div>
                          {v.data_source === 'verified' && (
                            <span className="flex items-center gap-1 text-xs text-[#2E7D32] bg-green-50 px-2 py-1 rounded-full">
                              <CheckCircle size={12} /> Verificato
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[#86868B] text-sm">Nessun vaccino registrato</p>
                  )}

                  {/* Recent exams */}
                  <h4 className="font-semibold text-[#1D1D1F] mt-6 mb-3">Ultimi esami</h4>
                  {exams?.length > 0 ? (
                    <div className="space-y-2">
                      {exams.slice(0, 5).map((e, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-[#F5F5F7] rounded-xl">
                          <div className="flex items-center gap-3">
                            <TestTube size={16} className="text-purple-600" />
                            <div>
                              <p className="font-medium text-sm">{e.exam_name}</p>
                              <p className="text-xs text-[#86868B]">
                                {new Date(e.exam_date).toLocaleDateString('it-IT')}
                              </p>
                            </div>
                          </div>
                          {e.result_status && (
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              e.result_status === 'normal' ? 'bg-green-100 text-green-700' :
                              e.result_status === 'abnormal' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {e.result_status === 'normal' ? 'Normale' : 
                               e.result_status === 'abnormal' ? 'Anomalo' : 'Critico'}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[#86868B] text-sm">Nessun esame registrato</p>
                  )}
                </div>
              </TabsContent>

              {/* Vaccines */}
              <TabsContent value="vaccines">
                <div className="bg-white rounded-3xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-[#1D1D1F]">Storico vaccinazioni</h3>
                    <Button 
                      onClick={() => setShowAddVaccine(true)}
                      size="sm"
                      className="bg-[#2E7D32] rounded-xl"
                    >
                      <Plus size={16} className="mr-1" /> Aggiungi
                    </Button>
                  </div>
                  
                  {vaccines?.length > 0 ? (
                    <div className="space-y-3">
                      {vaccines.map((v, i) => (
                        <div key={i} className="p-4 bg-[#F5F5F7] rounded-2xl">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-[#1D1D1F]">{v.name}</p>
                                {v.data_source === 'verified' && (
                                  <span className="flex items-center gap-1 text-xs text-[#2E7D32] bg-green-100 px-2 py-0.5 rounded-full">
                                    <CheckCircle size={10} /> Veterinario
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-[#86868B] mt-1">
                                {VACCINE_TYPES.find(t => t.value === v.vaccine_type)?.label || v.vaccine_type}
                              </p>
                            </div>
                            <p className="text-sm font-medium">
                              {new Date(v.date_administered).toLocaleDateString('it-IT')}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-4 mt-3 text-xs text-[#86868B]">
                            {v.expiry_date && (
                              <span>Scade: {new Date(v.expiry_date).toLocaleDateString('it-IT')}</span>
                            )}
                            {v.batch_number && <span>Lotto: {v.batch_number}</span>}
                            {v.vet_name && <span>Vet: {v.vet_name}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-[#86868B] py-8">Nessuna vaccinazione registrata</p>
                  )}
                </div>
              </TabsContent>

              {/* Exams */}
              <TabsContent value="exams">
                <div className="bg-white rounded-3xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-[#1D1D1F]">Storico esami</h3>
                    <Button 
                      onClick={() => setShowAddExam(true)}
                      size="sm"
                      className="bg-[#2E7D32] rounded-xl"
                    >
                      <Plus size={16} className="mr-1" /> Aggiungi
                    </Button>
                  </div>
                  
                  {exams?.length > 0 ? (
                    <div className="space-y-3">
                      {exams.map((e, i) => (
                        <div key={i} className="p-4 bg-[#F5F5F7] rounded-2xl">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-[#1D1D1F]">{e.exam_name}</p>
                                {e.data_source === 'verified' && (
                                  <span className="flex items-center gap-1 text-xs text-[#2E7D32] bg-green-100 px-2 py-0.5 rounded-full">
                                    <CheckCircle size={10} /> Veterinario
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-[#86868B] mt-1">
                                {EXAM_TYPES.find(t => t.value === e.exam_type)?.label || e.exam_type}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium">
                                {new Date(e.exam_date).toLocaleDateString('it-IT')}
                              </p>
                              {e.result_status && (
                                <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                                  e.result_status === 'normal' ? 'bg-green-100 text-green-700' :
                                  e.result_status === 'abnormal' ? 'bg-amber-100 text-amber-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {e.result_status === 'normal' ? 'Normale' : 
                                   e.result_status === 'abnormal' ? 'Anomalo' : 'Critico'}
                                </span>
                              )}
                            </div>
                          </div>
                          {e.result_summary && (
                            <p className="text-sm text-[#86868B] mt-2">{e.result_summary}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-[#86868B] py-8">Nessun esame registrato</p>
                  )}
                </div>
              </TabsContent>

              {/* Visits */}
              <TabsContent value="visits">
                <div className="bg-white rounded-3xl p-6 shadow-sm">
                  <h3 className="font-bold text-[#1D1D1F] mb-4">Storico visite</h3>
                  
                  {visits?.length > 0 ? (
                    <div className="space-y-3">
                      {visits.map((v, i) => (
                        <div key={i} className="flex items-center gap-4 p-4 bg-[#F5F5F7] rounded-2xl">
                          <div className="w-10 h-10 rounded-xl bg-[#2E7D32]/10 flex items-center justify-center">
                            <Stethoscope className="text-[#2E7D32]" size={20} />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-[#1D1D1F]">{v.visit_type}</p>
                            <p className="text-sm text-[#86868B]">{v.notes}</p>
                          </div>
                          <p className="text-sm text-[#86868B]">
                            {new Date(v.visit_date).toLocaleDateString('it-IT')}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-[#86868B] py-8">Nessuna visita registrata</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>

      {/* Add Vaccine Dialog */}
      <AddVaccineDialog 
        open={showAddVaccine}
        onClose={() => setShowAddVaccine(false)}
        petId={petId}
        onSuccess={() => {
          setShowAddVaccine(false);
          fetchPatientData();
          toast.success("Vaccino registrato con certificazione");
        }}
      />

      {/* Add Exam Dialog */}
      <AddExamDialog 
        open={showAddExam}
        onClose={() => setShowAddExam(false)}
        petId={petId}
        onSuccess={() => {
          setShowAddExam(false);
          fetchPatientData();
          toast.success("Esame registrato con certificazione");
        }}
      />

      {/* Add Appointment Dialog */}
      <AddAppointmentDialog 
        open={showAddAppointment}
        onClose={() => setShowAddAppointment(false)}
        petId={petId}
        onSuccess={() => {
          setShowAddAppointment(false);
          fetchPatientData();
          toast.success("Appuntamento creato");
        }}
      />
    </div>
  );
}

// Add Vaccine Dialog
function AddVaccineDialog({ open, onClose, petId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    vaccine_type: "",
    date_administered: "",
    expiry_date: "",
    batch_number: "",
    manufacturer: "",
    notes: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const token = localStorage.getItem('vet_token');
    try {
      const params = new URLSearchParams({
        name: form.name,
        vaccine_type: form.vaccine_type,
        date_administered: form.date_administered,
        ...(form.expiry_date && { expiry_date: form.expiry_date }),
        ...(form.batch_number && { batch_number: form.batch_number }),
        ...(form.manufacturer && { manufacturer: form.manufacturer }),
        ...(form.notes && { notes: form.notes })
      });

      const res = await fetch(`${API}/api/vet/patients/${petId}/vaccines?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (res.ok) {
        onSuccess();
        setForm({ name: "", vaccine_type: "", date_administered: "", expiry_date: "", batch_number: "", manufacturer: "", notes: "" });
      } else {
        const data = await res.json();
        toast.error(data.detail || "Errore");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-3xl p-0 border-0">
        <div className="p-6">
          <DialogHeader className="mb-6">
            <DialogTitle className="text-xl font-bold flex items-center gap-3">
              <Syringe className="text-[#2E7D32]" size={24} />
              Registra vaccinazione
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Nome vaccino *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                placeholder="Es. Nobivac DHPPi"
                className="h-12 rounded-xl"
                required
              />
            </div>

            <div>
              <Label className="text-sm font-medium mb-1.5 block">Tipo vaccino *</Label>
              <Select value={form.vaccine_type} onValueChange={(v) => setForm({...form, vaccine_type: v})}>
                <SelectTrigger className="h-12 rounded-xl">
                  <SelectValue placeholder="Seleziona tipo..." />
                </SelectTrigger>
                <SelectContent>
                  {VACCINE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Data somministrazione *</Label>
                <Input
                  type="date"
                  value={form.date_administered}
                  onChange={(e) => setForm({...form, date_administered: e.target.value})}
                  className="h-12 rounded-xl"
                  required
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Scadenza</Label>
                <Input
                  type="date"
                  value={form.expiry_date}
                  onChange={(e) => setForm({...form, expiry_date: e.target.value})}
                  className="h-12 rounded-xl"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Numero lotto</Label>
                <Input
                  value={form.batch_number}
                  onChange={(e) => setForm({...form, batch_number: e.target.value})}
                  placeholder="Es. A12345"
                  className="h-12 rounded-xl font-mono"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Produttore</Label>
                <Input
                  value={form.manufacturer}
                  onChange={(e) => setForm({...form, manufacturer: e.target.value})}
                  placeholder="Es. MSD"
                  className="h-12 rounded-xl"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-xl text-sm text-green-700">
              <CheckCircle size={16} />
              Questo vaccino sarà certificato con badge "Verificato dal veterinario"
            </div>

            <Button 
              type="submit"
              disabled={loading || !form.name || !form.vaccine_type || !form.date_administered}
              className="w-full h-12 bg-[#2E7D32] hover:bg-[#1B5E20] rounded-xl"
            >
              {loading ? "Salvataggio..." : "Registra vaccino"}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Add Exam Dialog
function AddExamDialog({ open, onClose, petId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    exam_type: "",
    exam_name: "",
    exam_date: "",
    result_summary: "",
    result_status: "",
    laboratory: "",
    notes: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const token = localStorage.getItem('vet_token');
    try {
      const params = new URLSearchParams({
        exam_type: form.exam_type,
        exam_name: form.exam_name,
        exam_date: form.exam_date,
        ...(form.result_summary && { result_summary: form.result_summary }),
        ...(form.result_status && { result_status: form.result_status }),
        ...(form.laboratory && { laboratory: form.laboratory }),
        ...(form.notes && { notes: form.notes })
      });

      const res = await fetch(`${API}/api/vet/patients/${petId}/exams?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (res.ok) {
        onSuccess();
        setForm({ exam_type: "", exam_name: "", exam_date: "", result_summary: "", result_status: "", laboratory: "", notes: "" });
      } else {
        const data = await res.json();
        toast.error(data.detail || "Errore");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-3xl p-0 border-0 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <DialogHeader className="mb-6">
            <DialogTitle className="text-xl font-bold flex items-center gap-3">
              <TestTube className="text-purple-600" size={24} />
              Registra esame
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Categoria esame *</Label>
              <Select value={form.exam_type} onValueChange={(v) => setForm({...form, exam_type: v})}>
                <SelectTrigger className="h-12 rounded-xl">
                  <SelectValue placeholder="Seleziona categoria..." />
                </SelectTrigger>
                <SelectContent>
                  {EXAM_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-medium mb-1.5 block">Nome esame *</Label>
              <Input
                value={form.exam_name}
                onChange={(e) => setForm({...form, exam_name: e.target.value})}
                placeholder="Es. Emocromo completo"
                className="h-12 rounded-xl"
                required
              />
            </div>

            <div>
              <Label className="text-sm font-medium mb-1.5 block">Data esame *</Label>
              <Input
                type="date"
                value={form.exam_date}
                onChange={(e) => setForm({...form, exam_date: e.target.value})}
                className="h-12 rounded-xl"
                required
              />
            </div>

            <div>
              <Label className="text-sm font-medium mb-1.5 block">Esito</Label>
              <Select value={form.result_status} onValueChange={(v) => setForm({...form, result_status: v})}>
                <SelectTrigger className="h-12 rounded-xl">
                  <SelectValue placeholder="Seleziona esito..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normale</SelectItem>
                  <SelectItem value="abnormal">Anomalo</SelectItem>
                  <SelectItem value="critical">Critico</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-medium mb-1.5 block">Riepilogo risultati</Label>
              <Textarea
                value={form.result_summary}
                onChange={(e) => setForm({...form, result_summary: e.target.value})}
                placeholder="Descrivi i risultati principali..."
                className="rounded-xl"
                rows={3}
              />
            </div>

            <div>
              <Label className="text-sm font-medium mb-1.5 block">Laboratorio</Label>
              <Input
                value={form.laboratory}
                onChange={(e) => setForm({...form, laboratory: e.target.value})}
                placeholder="Nome laboratorio"
                className="h-12 rounded-xl"
              />
            </div>

            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-xl text-sm text-green-700">
              <CheckCircle size={16} />
              Questo esame sarà certificato con badge "Verificato dal veterinario"
            </div>

            <Button 
              type="submit"
              disabled={loading || !form.exam_type || !form.exam_name || !form.exam_date}
              className="w-full h-12 bg-[#2E7D32] hover:bg-[#1B5E20] rounded-xl"
            >
              {loading ? "Salvataggio..." : "Registra esame"}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Add Appointment Dialog
function AddAppointmentDialog({ open, onClose, petId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    date: "",
    time: "",
    service_type: "",
    duration_minutes: 30,
    notes: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const token = localStorage.getItem('vet_token');
    try {
      const res = await fetch(`${API}/api/vet/appointments`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          pet_id: petId,
          ...form
        })
      });

      if (res.ok) {
        onSuccess();
        setForm({ date: "", time: "", service_type: "", duration_minutes: 30, notes: "" });
      } else {
        const data = await res.json();
        toast.error(data.detail || "Errore");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-3xl p-0 border-0">
        <div className="p-6">
          <DialogHeader className="mb-6">
            <DialogTitle className="text-xl font-bold flex items-center gap-3">
              <Calendar className="text-blue-600" size={24} />
              Prenota appuntamento
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Data *</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({...form, date: e.target.value})}
                  className="h-12 rounded-xl"
                  required
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Ora *</Label>
                <Input
                  type="time"
                  value={form.time}
                  onChange={(e) => setForm({...form, time: e.target.value})}
                  className="h-12 rounded-xl"
                  required
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium mb-1.5 block">Tipo visita *</Label>
              <Select value={form.service_type} onValueChange={(v) => setForm({...form, service_type: v})}>
                <SelectTrigger className="h-12 rounded-xl">
                  <SelectValue placeholder="Seleziona..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="visita">Visita generale</SelectItem>
                  <SelectItem value="vaccinazione">Vaccinazione</SelectItem>
                  <SelectItem value="controllo">Controllo</SelectItem>
                  <SelectItem value="emergenza">Emergenza</SelectItem>
                  <SelectItem value="chirurgia">Chirurgia</SelectItem>
                  <SelectItem value="esami">Esami</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-medium mb-1.5 block">Durata (minuti)</Label>
              <Select value={form.duration_minutes.toString()} onValueChange={(v) => setForm({...form, duration_minutes: parseInt(v)})}>
                <SelectTrigger className="h-12 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 min</SelectItem>
                  <SelectItem value="30">30 min</SelectItem>
                  <SelectItem value="45">45 min</SelectItem>
                  <SelectItem value="60">1 ora</SelectItem>
                  <SelectItem value="90">1.5 ore</SelectItem>
                  <SelectItem value="120">2 ore</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-medium mb-1.5 block">Note</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({...form, notes: e.target.value})}
                placeholder="Note aggiuntive..."
                className="rounded-xl"
                rows={2}
              />
            </div>

            <Button 
              type="submit"
              disabled={loading || !form.date || !form.time || !form.service_type}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 rounded-xl"
            >
              {loading ? "Creazione..." : "Crea appuntamento"}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
